// App.tsx

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, signInAnonymously,  UserCredential,
  getAdditionalUserInfo } from 'firebase/auth';
import { auth } from './firebase';

import LoginView from './components/common/LoginView';
import Top from './Top';
import AdAdmin from './components/admin/AdsManager';
import GiftCodeManager from './components/admin/GiftCodeManager';

import './App.css';
// UserContextを正しくインポートします
import { UserProvider, UserContext } from './services/user/LoginUserContext';

import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';

import { addMyQuizSelection } from './services/myQuizSelectionService';
import { fetchQuiz} from './services/quizService';
import { logger } from './utils/logger';

/**
 * 起動時のリダイレクトを処理するコンポーネント
 */
const RootRedirect = () => {
  // contextの型をチェックし、nullの場合はエラーを投げるかデフォルト値を扱う
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('RootRedirect must be used within a UserProvider');
  }
  const { user, loading } = context;

  // 認証状態の確認中はローディング画面を表示
  if (loading) {
    return <div>Loading...</div>;
  }

  // 確認後、ユーザーがいれば/Topへ、いなければ/loginへリダイレクト
  return user ? <Navigate to="/Top" /> : <Navigate to="/login" />;
};


// ProtectedRoutePropsの型定義
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }): React.ReactElement | null => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('ProtectedRoute must be used within a UserProvider');
  }
  const { user, loading } = context;

  if (loading) return <div>Loading...</div>;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

// AdminRoutePropsの型定義
interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }): React.ReactElement | null => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('AdminRoute must be used within a UserProvider');
  }
  // UserContextから取得するuserとuidの型を明示
  const { user, loading } = context;

  // uidはuserDataから取得
  const uid = user?.uid;
  const adminUids = [
    'NiwdN5zauHRPfvft9QUE0Ed1SIp1',
    'NiwdNwewefsdfsfvft9QUE0Ed1SIp2',
    'NiwdN5zauHRPfsfsfSIp3',
  ];

  if (loading) return <div>Loading...</div>;
  // userが存在し、かつuidがadminUidsに含まれているか確認
  if (!user || !uid || !adminUids.includes(uid)) return <Navigate to="/Top" />;

  return <>{children}</>;
};

function App() {
  const handleGoogleLogin = React.useCallback(async () => {
    await signInWithPopup(auth, new GoogleAuthProvider()).then(() => { });
  }, []);

  const handleAnonymousLogin = React.useCallback(async () => {
    await signInAnonymously(auth).then(() => { });
  }, []);

  return (
    <UserProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* ----- ここを修正 ----- */}
            {/* ルートパス "/" を新しいRootRedirectコンポーネントに任せる */}
            <Route path="/" element={<RootRedirect />} />
            
            <Route
              path="/login"
              element={<LoginView   onGoogleLogin={async () => {
                try {
                  const result: UserCredential = await signInWithPopup(
                    auth,
                    new GoogleAuthProvider()
                  );
                  const info = getAdditionalUserInfo(result);
                  const isNewUser = info?.isNewUser;
          
                  logger.info("isNewUser:" + isNewUser);
                  logger.info("info:" + info);
                  
                  if (isNewUser) {
                    const initialQuizIds = [
                      'rszBbfTtRV88aY45Y3cU',
                      'dVg38tZqXSR4NFbje1Ek',
                      'rBjj2JfGRyDd7zaxYTLZ'
                      // 今後追加するクイズIDをここに列挙
                      // 'anotherQuizId',
                      // 'moreQuizId'
                    ];
          
                    for (const quizId of initialQuizIds) {
                      const quiz = await fetchQuiz(quizId);
                      if (quiz) {
                        await addMyQuizSelection(result.user.uid, quiz);
                      }
                    }
                    logger.log('新規ユーザー: 初回クイズを追加しました');
                  }
                } catch (error) {
                  logger.error('Google ログイン失敗:', error);
                }
              }} onAnonymousLogin={handleAnonymousLogin} />}
            />

            <Route
              path="/Top"
              element={
                <ProtectedRoute>
                  <Top />
                </ProtectedRoute>
              }
            />
            {/* クイズディープリンクルート: quizIdをURLパラメータとして受け取る */}
            <Route
              path="/Top/:quizId"
              element={
                <ProtectedRoute>
                  <Top /> {/* quizIdはTopコンポーネント内でuseParamsを使って取得します */}
                </ProtectedRoute>
              }
            />

            <Route
              path="/AdAdmin"
              element={
                <AdminRoute>
                  <AdAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/GiftCodeAdmin"
              element={
                <AdminRoute>
                  <GiftCodeManager />
                </AdminRoute>
              }
            />
            {/* 利用規約とプライバシーポリシーのルート */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </div>
      </Router>
    </UserProvider>
  );
}

export default App;